// src/pages/LandingPage.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { keyframes } from '@mui/system';

const floatSlow = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const floatSlowAlt = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(8px) rotate(-2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

function PricingCard() {
  const [currency, setCurrency] = useState('ars');

  const handleCurrencyChange = (event, newCurrency) => {
    if (newCurrency !== null) {
      setCurrency(newCurrency);
    }
  };

  const price = currency === 'ars' ? '$5.000/mes' : '$5 USD/mes';

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: '#ffffff',
        width: '100%',
        maxWidth: 340,
      }}
    >
      {/* Banner con líneas decorativas */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mb: 1.5 }} />
        <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1.5 }}>
          7 DÍAS DE PRUEBA GRÁTIS
        </Typography>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.5 }} />
      </Box>

      {/* Features con checkmarks */}
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ✓ Acceso completo a toda la app
        </Typography>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ✓ Sin tarjeta requerida para comenzar
        </Typography>
      </Stack>

      {/* Línea divisoria */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mb: 2 }} />

      {/* Toggle y precio */}
      <ToggleButtonGroup
        value={currency}
        exclusive
        onChange={handleCurrencyChange}
        fullWidth
        sx={{
          mb: 2,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 500,
            border: '1px solid',
            borderColor: 'divider',
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
          },
        }}
      >
        <ToggleButton value="ars">ARS</ToggleButton>
        <ToggleButton value="usd">USD</ToggleButton>
      </ToggleButtonGroup>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {price}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        /mes después de tu prueba
      </Typography>

      {/* Métodos de pago */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Pagas con: MercadoPago o PayPal
        </Typography>
      </Box>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mb: 2 }} />

      <Button
        variant="contained"
        size="large"
        fullWidth
        component="a"
        href="https://app.calculana.com/register"
      >
        Comenzar prueba gratis
      </Button>
    </Paper>
  );
}

function LandingPage() {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#fdf6fb',
        overflow: 'hidden',
      }}
    >
      {/* Fondo con ovillos */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 0% 0%, rgba(255, 182, 193, 0.18), transparent 55%), radial-gradient(circle at 100% 100%, rgba(255, 192, 203, 0.16), transparent 55%)',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: { xs: '-20px', md: '5%' },
            right: { xs: '-40px', md: '4%' },
            width: { xs: 160, md: 240 },
            height: { xs: 160, md: 240 },
            borderRadius: '50%',
            overflow: 'hidden',
            animation: `${floatSlow} 5s ease-in-out infinite`,
          }}
        >
          <Box component="img" src="/yarn-hero-1.webp" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: '-10px', md: '8%' },
            right: { xs: '5%', md: '12%' },
            width: { xs: 130, md: 190 },
            height: { xs: 130, md: 190 },
            borderRadius: '50%',
            overflow: 'hidden',
            animation: `${floatSlowAlt} 5s ease-in-out infinite`,
          }}
        >
          <Box component="img" src="/yarn-hero-2.webp" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: '-40px', md: '-20px' },
            right: { xs: '-30px', md: '-10px' },
            width: { xs: 140, md: 180 },
            height: { xs: 140, md: 180 },
            borderRadius: '50%',
            overflow: 'hidden',
            opacity: 0.9,
            animation: `${floatSlowAlt} 7s ease-in-out infinite`,
          }}
        >
          <Box component="img" src="/yarn-hero-3.webp" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(253, 246, 251, 0.98) 0%, rgba(253, 246, 251, 0.94) 38%, rgba(253, 246, 251, 0.4) 68%, rgba(253, 246, 251, 0.15) 100%)',
          }}
        />
      </Box>

      {/* Contenido */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '100vh',
          py: { xs: 6, md: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-start' },
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          {/* Hero Section */}
          <Box sx={{ flex: { md: '0 0 58%' }, maxWidth: { md: '58%' } }}>
            <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }}>
              <Box component="header">
                <Box component="img" src="/logo.svg" alt="Calculana" sx={{ height: 40, mb: 1 }} />
              </Box>

              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.1,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Tejer es tu arte. Administrarlo no debería ser un caos.
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 540, textAlign: { xs: 'center', md: 'left' } }}
              >
                Calculana ayuda a tejedoras emprendedoras a calcular costos, organizar pedidos, controlar stock y manejar su negocio desde un solo lugar.
              </Typography>

              {/* CTA */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="https://app.calculana.com/register"
                >
                  Empezar gratis
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href="https://app.calculana.com/login"
                >
                  Ya uso Calculana
                </Button>

                <Button
                  variant="text"
                  size="large"
                  component="a"
                  href="https://calculana.com/blog/"
                >
                  Leer el blog
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  maxWidth: 540,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  ✦ Sin planillas
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✦ Sin conocimientos técnicos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✦ Pensado para tejedoras
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Pricing Card Section */}
          <Box
            sx={{
              flex: { md: '0 0 38%' },
              maxWidth: { md: '38%' },
              width: { xs: '100%', md: 'auto' },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <PricingCard />
          </Box>
        </Box>

        <Box component="footer">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', mt: 4, display: 'block' }}
          >
            © {new Date().getFullYear()} Calculana - Todo tu emprendimiento de tejido, en un solo lugar.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default LandingPage;
